import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import LoadingIcons from 'react-loading-icons';
import useRequest from '../../../../hooks/useRequest';
import {
  BotComplexParams,
  BotTimeFrames,
  TimeFrame,
} from '../../../Detail/types';
import { SingleProfitCalculationApiResponse } from '../../types';
import BasicInfoForm from '../Form/Basic';
import TimeFramesInfo from '../Form/TimeFrames';
import ComplexParamsForm from '../Form/ComplexParams';
import Result from './Result';
import './index.css';
import { formatParam } from '../../../Control/Content/helpers';
import { colors } from '../../../../colors';
import { useLocation } from 'react-router-dom';
import {
  ProfitByPeriodApiResponse,
  ProfitCalculationProfitData,
} from '../../../Detail/Charts/types';
import { Exchange } from '../../../../types/exchange';

import ServicePaths from '../../../../bridge/utils/ServicePaths';

interface PassedProps {
  baseAsset: string;
  quoteAsset: string;
  startDate: number;
  timeFrames: BotTimeFrames;
  complexParams: BotComplexParams;
  exchange: Exchange;
  latestOrder: string;
  useTakeProfitTransactions: boolean;
}

interface Props {
  setProfitByPeriodData: React.Dispatch<
    React.SetStateAction<ProfitByPeriodApiResponse | undefined>
  >;
  setProfitData: React.Dispatch<
    React.SetStateAction<ProfitCalculationProfitData | undefined>
  >;
}

const formatDateToUsableInputValue = (date: Date) => date.toJSON().slice(0, 10);

const SingleProfitCalculation = (props: Props) => {
  const { setProfitByPeriodData, setProfitData } = props;
  const { state: passedProps }: { state: PassedProps | undefined } =
    useLocation();

  const [baseAsset, setBaseAsset] = useState(passedProps?.baseAsset || '');
  const [quoteAsset, setQuoteAsset] = useState(
    passedProps?.quoteAsset || 'USDT',
  );
  const [exchange, setExchange] = useState(
    passedProps?.exchange || Object.values(Exchange)[0],
  );

  const [startDate, setStartDate] = useState(
    !!passedProps?.startDate
      ? formatDateToUsableInputValue(new Date(passedProps.startDate))
      : '2017-01-01',
  );
  const [endDate, setEndDate] = useState(
    formatDateToUsableInputValue(
      new Date(new Date().valueOf() + 1000 * 60 * 60 * 24),
    ),
  );

  const [higherTimeFrame, setHigherTimeFrame] = useState(
    passedProps?.timeFrames?.higher || TimeFrame.TwoDays,
  );
  const [shorterTimeFrame, setShorterTimeFrame] = useState(
    passedProps?.timeFrames?.shorter || TimeFrame.TwelveHours,
  );
  const [multiOrderTimeFrame, setMultiOrderTimeFrame] = useState(
    passedProps?.timeFrames?.multiOrder || TimeFrame.ThreeHours,
  );

  const [cbrt, setCbrt] = useState(
    !!passedProps?.complexParams?.cbrt
      ? passedProps?.complexParams?.cbrt.toString()
      : '0.155',
  );
  const [shorterTimeFrameInterval, setShorterTimeFrameInterval] = useState(
    !!passedProps?.complexParams?.shorterTimeFrameInterval
      ? passedProps?.complexParams?.shorterTimeFrameInterval.toString()
      : '1',
  );
  const [latestOrder, setLatestOrder] = useState(
    !!passedProps?.latestOrder ? passedProps.latestOrder : 'SELL',
  );
  const [supportCbrtRange, setSupportCbrtRange] = useState(
    passedProps?.complexParams?.supportCbrtRange ?? true,
  );
  const [supportMrcState, setSupportMrcState] = useState(
    passedProps?.complexParams?.supportMrcState ?? true,
  );
  const [useTakeProfitTransactions, setUseTakeProfitTransactions] =
    useState<boolean>(
      !!passedProps?.useTakeProfitTransactions
        ? passedProps.useTakeProfitTransactions
        : true,
    );

  const [shouldFetch, setShouldFetch] = useState(!!passedProps);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isCollapsable, setIsCollapsable] = useState(false);

  const params = useMemo(
    () => ({
      baseAsset,
      quoteAsset,
      exchange,
      startDate: new Date(startDate).valueOf(),
      endDate: new Date(endDate).valueOf(),
      higherTimeFrame,
      shorterTimeFrame,
      multiOrderTimeFrame,
      cbrt: formatParam(cbrt),
      shorterTimeFrameInterval: formatParam(shorterTimeFrameInterval),
      supportCbrtRange,
      supportMrcState,
      latestOrder,
      useTakeProfitTransactions,
    }),
    [
      baseAsset,
      quoteAsset,
      exchange,
      startDate,
      endDate,
      higherTimeFrame,
      shorterTimeFrame,
      multiOrderTimeFrame,
      cbrt,
      shorterTimeFrameInterval,
      supportCbrtRange,
      supportMrcState,
      latestOrder,
      useTakeProfitTransactions,
    ],
  );

  const options: { method: 'POST'; shouldFetch: boolean } = useMemo(
    () => ({
      method: 'POST',
      shouldFetch,
    }),
    [shouldFetch],
  );

  const {
    data,
    isValidating,
  }: {
    data: SingleProfitCalculationApiResponse | undefined;
    isValidating: boolean;
  } = useRequest(
    ServicePaths.PROFIT_CALCULATIONS.GetSingleProfitCalculation,
    params,
    options,
  );

  const toggleCollapse = useCallback(() => {
    if (!isCollapsable) {
      return;
    }

    setIsCollapsed(!isCollapsed);
  }, [isCollapsable, isCollapsed]);

  useEffect(() => {
    if (data && !isCollapsable) {
      setIsCollapsable(true);
      setIsCollapsed(true);
    }
  }, [data, isCollapsable]);

  useEffect(() => {
    if (!data) {
      return;
    }

    setProfitData({
      stable: data.totalTradingStableProfit,
      asset: data.totalTradingAssetProfit,
      price: data.totalWithoutTradingStableProfit,
      priceSinceFirstTransaction:
        data.totalWithoutTradingStableProfitSinceFirstTransaction,
    });

    setProfitByPeriodData({
      stable: Object.entries(data.endBalanceInUsdtHistory).map(
        ([timestamp, value]) => ({ timestamp: parseInt(timestamp, 10), value }),
      ),
      asset: Object.entries(data.endBalanceInAssetHistory).map(
        ([timestamp, value]) => ({ timestamp: parseInt(timestamp, 10), value }),
      ),
    });
  }, [data, setProfitByPeriodData, setProfitData]);

  return (
    <>
      <div
        className="single-profit-calculation-header"
        onClick={toggleCollapse}
      >
        <h3 className="single-profit-calculation-label">Form:</h3>
        {isValidating ? (
          <div className="single-profit-calculation-loader">
            <LoadingIcons.Grid width={20} height={20} />
          </div>
        ) : (
          isCollapsable &&
          (isCollapsed ? (
            <FaChevronDown
              color={colors.white}
              size={24}
              className={'chevron chevron-down'}
            />
          ) : (
            <FaChevronUp
              color={colors.white}
              size={24}
              className={'chevron chevron-down'}
            />
          ))
        )}
      </div>

      {!isCollapsed && (
        <div className="single-profit-calculation-content">
          <BasicInfoForm
            baseAsset={baseAsset}
            setBaseAsset={setBaseAsset}
            quoteAsset={quoteAsset}
            setQuoteAsset={setQuoteAsset}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            setShouldFetch={setShouldFetch}
            exchange={exchange}
            setExchange={setExchange}
          />
          <TimeFramesInfo
            higherTimeFrame={higherTimeFrame}
            setHigherTimeFrame={setHigherTimeFrame}
            shorterTimeFrame={shorterTimeFrame}
            setShorterTimeFrame={setShorterTimeFrame}
            multiOrderTimeFrame={multiOrderTimeFrame}
            setMultiOrderTimeFrame={setMultiOrderTimeFrame}
          />
          <ComplexParamsForm
            cbrt={cbrt}
            setCbrt={setCbrt}
            shorterTimeFrameInterval={shorterTimeFrameInterval}
            setShorterTimeFrameInterval={setShorterTimeFrameInterval}
            latestOrder={latestOrder}
            setLatestOrder={setLatestOrder}
            supportCbrtRange={supportCbrtRange}
            setSupportCbrtRange={setSupportCbrtRange}
            supportMrcState={supportMrcState}
            setSupportMrcState={setSupportMrcState}
            setShouldFetch={setShouldFetch}
            useTakeProfitTransactions={useTakeProfitTransactions}
            setUseTakeProfitTransactions={setUseTakeProfitTransactions}
          />
        </div>
      )}

      <div className="single-profit-calcultion-divider" />

      <Result data={data} />
    </>
  );
};

export default SingleProfitCalculation;
